'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { X, Euro, ChevronDown, CircleCheckBig } from 'lucide-react';

interface InvestmentCreationWidgetProps {
  autoPlay?: boolean;
  className?: string;
}

// Animation state for the form fill sequence
type AnimationPhase =
  | 'initial'
  | 'form-appear'
  | 'promotor-focus'
  | 'promotor-dropdown'
  | 'promotor-selected'
  | 'cuota-loading'
  | 'cuota-focus'
  | 'cuota-dropdown'
  | 'cuota-selected'
  | 'inversor-focus'
  | 'inversor-dropdown'
  | 'inversor-selected'
  | 'amount-focus'
  | 'amount-typing'
  | 'rentabilidad-focus'
  | 'rentabilidad-typing'
  | 'form-complete'
  | 'button-highlight'
  | 'pause';

// Mock data matching real Hulahoop domain
const mockPromoters = [
  { value: 'aurora-films', label: 'Aurora Films S.L.' },
  { value: 'stellar-prod', label: 'Stellar Productions' },
  { value: 'nova-cine', label: 'Nova Cine S.A.' },
];

const mockCuotas = [
  { value: 'cuota-2025', label: 'Cuota Fiscal 2025' },
  { value: 'cuota-2026', label: 'Cuota Fiscal 2026' },
];

const mockInvestors = [
  { value: 'carlos-mendez', label: 'Carlos Mendez Garcia' },
  { value: 'maria-garcia', label: 'Maria Garcia Lopez' },
  { value: 'tech-ventures', label: 'Tech Ventures S.L.' },
];

// Quota bar colors matching the real app
const quotaBarColors = {
  completed: '#27272a', // zinc-800 - financiado
  reserved: '#8b5cf6', // violet-500 - reservado
  new: '#3b82f6', // blue-500 - nueva inversion
  available: '#e4e4e7', // zinc-200 - disponible
};

// Fixed values for the capital progress bar (in euros)
const TOTAL_CAPACITY = 500000;
const COMPLETED_AMOUNT = 175000; // 35% of 500k
const RESERVED_AMOUNT = 75000; // 15% of 500k
const NEW_INVESTMENT = 25000; // The amount being typed
const RENTABILIDAD = 0.2; // 20%

export function InvestmentCreationWidget({
  autoPlay = true,
  className,
}: InvestmentCreationWidgetProps) {
  const [phase, setPhase] = useState<AnimationPhase>('initial');
  const [isFormVisible, setIsFormVisible] = useState(false);

  // Form state
  const [selectedPromotor, setSelectedPromotor] = useState<string | null>(null);
  const [selectedCuota, setSelectedCuota] = useState<string | null>(null);
  const [selectedInversor, setSelectedInversor] = useState<string | null>(null);
  const [amountValue, setAmountValue] = useState('');
  const [rentabilidadValue, setRentabilidadValue] = useState('');

  // Dropdown open states
  const [promotorDropdownOpen, setPromotorDropdownOpen] = useState(false);
  const [cuotaDropdownOpen, setCuotaDropdownOpen] = useState(false);
  const [inversorDropdownOpen, setInversorDropdownOpen] = useState(false);

  // Cuota bar animation - tracks the "new investment" portion only
  const [showNewInvestment, setShowNewInvestment] = useState(false);
  const [newInvestmentProgress, setNewInvestmentProgress] = useState(0); // 0 to 1

  const animationRef = useRef<boolean>(true);

  useEffect(() => {
    if (!autoPlay) return;
    animationRef.current = true;

    const runSequence = async () => {
      // Reset all state
      setPhase('initial');
      setIsFormVisible(false);
      setSelectedPromotor(null);
      setSelectedCuota(null);
      setSelectedInversor(null);
      setAmountValue('');
      setRentabilidadValue('');
      setPromotorDropdownOpen(false);
      setCuotaDropdownOpen(false);
      setInversorDropdownOpen(false);
      setShowNewInvestment(false);
      setNewInvestmentProgress(0);

      await delay(500);
      if (!animationRef.current) return;

      // 1. Form appears
      setPhase('form-appear');
      setIsFormVisible(true);
      await delay(1200);
      if (!animationRef.current) return;

      // 2. Promotor field focus
      setPhase('promotor-focus');
      await delay(400);
      if (!animationRef.current) return;

      // 3. Promotor dropdown opens
      setPhase('promotor-dropdown');
      setPromotorDropdownOpen(true);
      await delay(800);
      if (!animationRef.current) return;

      // 4. Promotor selected
      setPhase('promotor-selected');
      setSelectedPromotor('aurora-films');
      setPromotorDropdownOpen(false);
      await delay(400);
      if (!animationRef.current) return;

      // 5. Cuota loading (brief)
      setPhase('cuota-loading');
      await delay(600);
      if (!animationRef.current) return;

      // 6. Cuota field focus
      setPhase('cuota-focus');
      await delay(400);
      if (!animationRef.current) return;

      // 7. Cuota dropdown opens
      setPhase('cuota-dropdown');
      setCuotaDropdownOpen(true);
      await delay(800);
      if (!animationRef.current) return;

      // 8. Cuota selected (2026)
      setPhase('cuota-selected');
      setSelectedCuota('cuota-2026');
      setCuotaDropdownOpen(false);
      await delay(600);
      if (!animationRef.current) return;

      // 9. Inversor field focus
      setPhase('inversor-focus');
      await delay(400);
      if (!animationRef.current) return;

      // 10. Inversor dropdown opens
      setPhase('inversor-dropdown');
      setInversorDropdownOpen(true);
      await delay(800);
      if (!animationRef.current) return;

      // 11. Inversor selected
      setPhase('inversor-selected');
      setSelectedInversor('carlos-mendez');
      setInversorDropdownOpen(false);
      await delay(400);
      if (!animationRef.current) return;

      // 12. Amount field focus
      setPhase('amount-focus');
      await delay(300);
      if (!animationRef.current) return;

      // 13. Type amount with typewriter effect
      setPhase('amount-typing');
      setShowNewInvestment(true);
      const targetAmount = '25.000';
      for (let i = 0; i <= targetAmount.length; i++) {
        if (!animationRef.current) return;
        setAmountValue(targetAmount.slice(0, i));
        // Animate the "new investment" portion of the bar
        setNewInvestmentProgress(i / targetAmount.length);
        await delay(80);
      }
      await delay(300);
      if (!animationRef.current) return;

      // 14. Rentabilidad field focus
      setPhase('rentabilidad-focus');
      await delay(300);
      if (!animationRef.current) return;

      // 15. Type rentabilidad (20%)
      setPhase('rentabilidad-typing');
      const targetRent = '20';
      for (let i = 0; i <= targetRent.length; i++) {
        if (!animationRef.current) return;
        setRentabilidadValue(targetRent.slice(0, i));
        await delay(100);
      }
      await delay(400);
      if (!animationRef.current) return;

      // 16. Form complete state
      setPhase('form-complete');
      await delay(800);
      if (!animationRef.current) return;

      // 17. Button highlight
      setPhase('button-highlight');
      await delay(1500);
      if (!animationRef.current) return;

      // 18. Pause before loop
      setPhase('pause');
      await delay(2000);
      if (!animationRef.current) return;

      // Loop
      if (animationRef.current) runSequence();
    };

    runSequence();
    return () => {
      animationRef.current = false;
    };
  }, [autoPlay]);

  // Helper to determine if a field is focused
  const isFieldFocused = (fieldPhases: AnimationPhase[]): boolean => {
    return fieldPhases.includes(phase);
  };

  // Calculate values for display - all coherent and adding up correctly
  const completedPercent = (COMPLETED_AMOUNT / TOTAL_CAPACITY) * 100; // 35%
  const reservedPercent = (RESERVED_AMOUNT / TOTAL_CAPACITY) * 100; // 15%
  const newPercent = (NEW_INVESTMENT / TOTAL_CAPACITY) * 100 * newInvestmentProgress; // 0-5%
  const currentNewAmount = NEW_INVESTMENT * newInvestmentProgress;
  const availableAmount = TOTAL_CAPACITY - COMPLETED_AMOUNT - RESERVED_AMOUNT - currentNewAmount;

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className={className}>
      {/* Direct form card - centered with FIXED dimensions */}
      <div className="flex justify-center">
        <AnimatePresence>
          {isFormVisible && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="bg-white rounded-lg shadow-xl border border-zinc-200 flex flex-col"
              style={{ width: '560px', height: '350px' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                <h2 className="text-xs font-semibold text-zinc-900">Agregar Nueva Inversion</h2>
                <button className="p-1 rounded hover:bg-zinc-100 transition-colors">
                  <X size={14} className="text-zinc-500" />
                </button>
              </div>

              {/* Content - flex-1 to fill remaining space */}
              <div className="p-3 flex-1 overflow-hidden">
                <div className="flex gap-4 h-full">
                  {/* Form Fields - fixed width */}
                  <div className="w-[180px] space-y-2 flex-shrink-0">
                    {/* Promotor Field */}
                    <div>
                      <label className="block text-[10px] font-medium text-zinc-700 mb-0.5">
                        Promotor *
                      </label>
                      <div className="relative">
                        <motion.button
                          type="button"
                          animate={{
                            boxShadow: isFieldFocused(['promotor-focus', 'promotor-dropdown'])
                              ? '0 0 0 2px rgba(24, 24, 27, 0.15)'
                              : '0 0 0 0px transparent',
                            borderColor: isFieldFocused(['promotor-focus', 'promotor-dropdown'])
                              ? '#18181b'
                              : '#e4e4e7',
                          }}
                          transition={{ duration: 0.2 }}
                          className={`w-full px-2 py-1 text-[10px] text-left border rounded flex items-center justify-between transition-colors ${
                            selectedPromotor ? 'bg-zinc-50' : 'bg-white'
                          }`}
                        >
                          <span
                            className={`truncate ${selectedPromotor ? 'text-zinc-900' : 'text-zinc-500'}`}
                          >
                            {selectedPromotor
                              ? mockPromoters.find((p) => p.value === selectedPromotor)?.label
                              : 'Buscar promotor...'}
                          </span>
                          <ChevronDown
                            size={10}
                            className={`text-zinc-400 transition-transform duration-200 flex-shrink-0 ml-1 ${
                              promotorDropdownOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </motion.button>

                        {/* Promotor Dropdown */}
                        <AnimatePresence>
                          {promotorDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15 }}
                              className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded shadow-lg max-h-24 overflow-auto"
                            >
                              {mockPromoters.map((option, index) => (
                                <motion.button
                                  key={option.value}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                  className={`w-full px-2 py-1 text-[10px] text-left hover:bg-zinc-50 ${
                                    option.value === 'aurora-films' ? 'bg-zinc-50' : ''
                                  }`}
                                >
                                  {option.label}
                                </motion.button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Cuota Fiscal Field */}
                    <div className={!selectedPromotor ? 'opacity-50' : ''}>
                      <label className="block text-[10px] font-medium text-zinc-700 mb-0.5">
                        Cuota Fiscal *
                      </label>
                      {phase === 'cuota-loading' ? (
                        <div className="text-center py-2 border border-zinc-200 rounded">
                          <div className="flex justify-center mb-0.5">
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                              className="w-2.5 h-2.5 border-2 border-zinc-300 border-t-zinc-600 rounded-full"
                            />
                          </div>
                          <p className="text-[9px] text-zinc-500">Cargando...</p>
                        </div>
                      ) : (
                        <div className="relative">
                          <motion.button
                            type="button"
                            animate={{
                              boxShadow: isFieldFocused(['cuota-focus', 'cuota-dropdown'])
                                ? '0 0 0 2px rgba(24, 24, 27, 0.15)'
                                : '0 0 0 0px transparent',
                              borderColor: isFieldFocused(['cuota-focus', 'cuota-dropdown'])
                                ? '#18181b'
                                : '#e4e4e7',
                            }}
                            transition={{ duration: 0.2 }}
                            className={`w-full px-2 py-1 text-[10px] text-left border rounded flex items-center justify-between transition-colors ${
                              selectedCuota ? 'bg-zinc-50' : 'bg-white'
                            } ${!selectedPromotor ? 'cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`truncate ${selectedCuota ? 'text-zinc-900' : 'text-zinc-500'}`}
                            >
                              {selectedCuota
                                ? mockCuotas.find((c) => c.value === selectedCuota)?.label
                                : 'Seleccionar cuota'}
                            </span>
                            <ChevronDown
                              size={10}
                              className={`text-zinc-400 transition-transform duration-200 flex-shrink-0 ml-1 ${
                                cuotaDropdownOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </motion.button>

                          {/* Cuota Dropdown */}
                          <AnimatePresence>
                            {cuotaDropdownOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.15 }}
                                className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded shadow-lg max-h-24 overflow-auto"
                              >
                                {mockCuotas.map((option, index) => (
                                  <motion.button
                                    key={option.value}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`w-full px-2 py-1 text-[10px] text-left hover:bg-zinc-50 ${
                                      option.value === 'cuota-2026' ? 'bg-zinc-50' : ''
                                    }`}
                                  >
                                    {option.label}
                                  </motion.button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>

                    {/* Inversor Field */}
                    <div className={!selectedCuota ? 'opacity-50' : ''}>
                      <label className="block text-[10px] font-medium text-zinc-700 mb-0.5">
                        Inversor *
                      </label>
                      <div className="relative">
                        <motion.button
                          type="button"
                          animate={{
                            boxShadow: isFieldFocused(['inversor-focus', 'inversor-dropdown'])
                              ? '0 0 0 2px rgba(24, 24, 27, 0.15)'
                              : '0 0 0 0px transparent',
                            borderColor: isFieldFocused(['inversor-focus', 'inversor-dropdown'])
                              ? '#18181b'
                              : '#e4e4e7',
                          }}
                          transition={{ duration: 0.2 }}
                          className={`w-full px-2 py-1 text-[10px] text-left border rounded flex items-center justify-between transition-colors ${
                            selectedInversor ? 'bg-zinc-50' : 'bg-white'
                          } ${!selectedCuota ? 'cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`truncate ${selectedInversor ? 'text-zinc-900' : 'text-zinc-500'}`}
                          >
                            {selectedInversor
                              ? mockInvestors.find((i) => i.value === selectedInversor)?.label
                              : 'Seleccionar inversor'}
                          </span>
                          <ChevronDown
                            size={10}
                            className={`text-zinc-400 transition-transform duration-200 flex-shrink-0 ml-1 ${
                              inversorDropdownOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </motion.button>

                        {/* Inversor Dropdown */}
                        <AnimatePresence>
                          {inversorDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15 }}
                              className="absolute z-50 w-full mt-0.5 bg-white border border-zinc-200 rounded shadow-lg max-h-24 overflow-auto"
                            >
                              {mockInvestors.map((option, index) => (
                                <motion.button
                                  key={option.value}
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                  className={`w-full px-2 py-1 text-[10px] text-left hover:bg-zinc-50 ${
                                    option.value === 'carlos-mendez' ? 'bg-zinc-50' : ''
                                  }`}
                                >
                                  {option.label}
                                </motion.button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>

                    {/* Amount and Rentabilidad Fields */}
                    <div
                      className={`grid grid-cols-2 gap-2 ${!selectedInversor ? 'opacity-50' : ''}`}
                    >
                      {/* Amount Field */}
                      <div>
                        <label className="block text-[10px] font-medium text-zinc-700 mb-0.5">
                          Inversion *
                        </label>
                        <div className="relative">
                          <Euro
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-zinc-400"
                            size={10}
                          />
                          <motion.input
                            type="text"
                            readOnly
                            value={amountValue}
                            animate={{
                              boxShadow: isFieldFocused(['amount-focus', 'amount-typing'])
                                ? '0 0 0 2px rgba(24, 24, 27, 0.15)'
                                : '0 0 0 0px transparent',
                              borderColor: isFieldFocused(['amount-focus', 'amount-typing'])
                                ? '#18181b'
                                : '#e4e4e7',
                            }}
                            transition={{ duration: 0.2 }}
                            className={`w-full pl-6 pr-2 py-1 text-[10px] border rounded focus:outline-none transition-colors ${
                              amountValue ? 'bg-zinc-50' : 'bg-white'
                            }`}
                            placeholder="0"
                          />
                          {/* Blinking cursor */}
                          {isFieldFocused(['amount-focus', 'amount-typing']) && (
                            <motion.span
                              animate={{ opacity: [1, 0] }}
                              transition={{ duration: 0.5, repeat: Infinity }}
                              className="absolute text-zinc-900 text-[10px]"
                              style={{
                                left: `${24 + amountValue.length * 5}px`,
                                top: '50%',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              |
                            </motion.span>
                          )}
                        </div>
                      </div>

                      {/* Rentabilidad Field */}
                      <div>
                        <label className="block text-[10px] font-medium text-zinc-700 mb-0.5">
                          % Rent. *
                        </label>
                        <div className="relative">
                          <motion.input
                            type="text"
                            readOnly
                            value={rentabilidadValue}
                            animate={{
                              boxShadow: isFieldFocused([
                                'rentabilidad-focus',
                                'rentabilidad-typing',
                              ])
                                ? '0 0 0 2px rgba(24, 24, 27, 0.15)'
                                : '0 0 0 0px transparent',
                              borderColor: isFieldFocused([
                                'rentabilidad-focus',
                                'rentabilidad-typing',
                              ])
                                ? '#18181b'
                                : '#e4e4e7',
                            }}
                            transition={{ duration: 0.2 }}
                            className={`w-full px-2 py-1 text-[10px] border rounded focus:outline-none transition-colors ${
                              rentabilidadValue ? 'bg-zinc-50' : 'bg-white'
                            }`}
                            placeholder="0"
                          />
                          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-400 text-[10px]">
                            %
                          </span>
                          {/* Blinking cursor */}
                          {isFieldFocused(['rentabilidad-focus', 'rentabilidad-typing']) && (
                            <motion.span
                              animate={{ opacity: [1, 0] }}
                              transition={{ duration: 0.5, repeat: Infinity }}
                              className="absolute text-zinc-900 text-[10px]"
                              style={{
                                left: `${8 + rentabilidadValue.length * 5}px`,
                                top: '50%',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              |
                            </motion.span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Capital Progress Card - ALWAYS visible, fixed size */}
                  <div className="flex-1 flex flex-col">
                    <div className="bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 rounded-lg p-3 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[11px] font-semibold text-zinc-900">
                          Capacidad de Inversion
                        </h3>
                        <span className="text-[11px] font-bold text-zinc-900">
                          {selectedCuota ? formatCurrency(TOTAL_CAPACITY) : '--'}
                        </span>
                      </div>

                      {/* Progress bar - always visible */}
                      <div
                        className="w-full h-6 rounded-md overflow-hidden relative shadow-inner mb-2"
                        style={{ backgroundColor: quotaBarColors.available }}
                      >
                        {selectedCuota && (
                          <>
                            {/* Completed quota */}
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${completedPercent}%` }}
                              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                              className="absolute inset-y-0 left-0"
                              style={{ backgroundColor: quotaBarColors.completed }}
                            />
                            {/* Reserved quota */}
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${reservedPercent}%` }}
                              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                              className="absolute inset-y-0"
                              style={{
                                left: `${completedPercent}%`,
                                backgroundColor: quotaBarColors.reserved,
                              }}
                            />
                            {/* New investment - with flicker animation */}
                            {showNewInvestment && newPercent > 0 && (
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{
                                  width: `${newPercent}%`,
                                  opacity: [1, 0.7, 1],
                                }}
                                transition={{
                                  width: { duration: 0.1 },
                                  opacity: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
                                }}
                                className="absolute inset-y-0"
                                style={{
                                  left: `${completedPercent + reservedPercent}%`,
                                  backgroundColor: quotaBarColors.new,
                                }}
                              />
                            )}
                          </>
                        )}
                      </div>

                      {/* Legend - horizontal compact layout */}
                      <div className="flex-1 flex flex-col justify-between">
                        {selectedCuota ? (
                          <>
                            {/* Top row: Financiado + Reservado */}
                            <div className="flex gap-4">
                              <div className="flex items-center gap-1 text-[10px]">
                                <div
                                  className="w-2.5 h-2.5 rounded"
                                  style={{ backgroundColor: quotaBarColors.completed }}
                                />
                                <span className="text-zinc-500">Financiado</span>
                                <span className="font-semibold text-zinc-900">
                                  {formatCurrency(COMPLETED_AMOUNT)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-[10px]">
                                <div
                                  className="w-2.5 h-2.5 rounded"
                                  style={{ backgroundColor: quotaBarColors.reserved }}
                                />
                                <span className="text-zinc-500">Reservado</span>
                                <span className="font-semibold text-zinc-900">
                                  {formatCurrency(RESERVED_AMOUNT)}
                                </span>
                              </div>
                            </div>

                            {/* Middle row: Nueva inversion (when active) */}
                            <div className="min-h-[32px] flex items-center">
                              {showNewInvestment && currentNewAmount > 0 ? (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="flex items-center gap-3"
                                >
                                  <div className="flex items-center gap-1 text-[10px]">
                                    <div
                                      className="w-2.5 h-2.5 rounded"
                                      style={{ backgroundColor: quotaBarColors.new }}
                                    />
                                    <span className="text-zinc-500">Nueva</span>
                                    <span className="font-semibold text-blue-600">
                                      {formatCurrency(currentNewAmount)}
                                    </span>
                                  </div>
                                  <div className="text-[9px] text-zinc-400">
                                    Deduccion:{' '}
                                    <span className="font-medium text-zinc-600">
                                      {formatCurrency(currentNewAmount * (1 + RENTABILIDAD))}
                                    </span>
                                  </div>
                                </motion.div>
                              ) : (
                                <div className="text-[10px] text-zinc-400">--</div>
                              )}
                            </div>

                            {/* Bottom row: Disponible */}
                            <div className="flex items-center gap-1 text-[10px] pt-1 border-t border-zinc-100">
                              <div
                                className="w-2.5 h-2.5 rounded border border-zinc-300"
                                style={{ backgroundColor: quotaBarColors.available }}
                              />
                              <span className="text-zinc-500">Disponible</span>
                              <span className="font-semibold text-zinc-900">
                                {formatCurrency(availableAmount)}
                              </span>
                            </div>
                          </>
                        ) : (
                          /* Empty placeholder when no cuota selected */
                          <div className="flex-1 flex items-center justify-center">
                            <span className="text-[10px] text-zinc-400">
                              Selecciona una cuota fiscal
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-1.5 px-3 py-2 border-t border-zinc-200">
                <button className="px-2 py-0.5 text-[10px] font-medium text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50 shadow-sm transition-colors">
                  Cancelar
                </button>
                <motion.button
                  animate={{
                    boxShadow:
                      phase === 'button-highlight'
                        ? '0 0 0 3px rgba(22, 163, 74, 0.2)'
                        : '0 0 0 0px transparent',
                    scale: phase === 'button-highlight' ? 1.02 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-white rounded shadow-sm transition-colors ${
                    amountValue && rentabilidadValue
                      ? 'bg-green-600 border border-green-600 hover:bg-green-700'
                      : 'bg-green-600/50 border border-green-600/50 cursor-not-allowed'
                  }`}
                >
                  <CircleCheckBig size={12} />
                  Agregar Inversion
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default InvestmentCreationWidget;
